import type { ReactNode } from 'react';
import { Building2, History, Hotel, MapPin, Plane } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@voll-intelligence/ui-kit/ui/command';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';

/**
 * O PAINEL DE LUGAR do DS — a metade "lista" do campo de endereço, extraída do
 * `Onde` do hotel para servir também às praças do aéreo (padronização do
 * combobox de lugar). A receita da LINHA é a do protótipo, a mesma da
 * `LinhaDaPraca` do aéreo e do `PainelDeLugares` do hotel: glifo do que a linha
 * entrega + título forte + legenda fraca, e só a legenda e o título truncam.
 *
 * Quem digita NÃO mora aqui: o input vive no campo (busca no campo, decisão do
 * designer 2026-08-19) e o `Command` root que liga os dois é de quem compõe —
 * o cmdk exige um root só para setas/Enter/Esc atravessarem do input pra lista.
 */

/** Uma linha do painel. `description` é a segunda linha já em prosa
 *  (`tipo · endereço` no hotel, `aeroporto · país` no voo) — quem a compõe é o
 *  host, que conhece o vocabulário e o idioma. `icon` é o glifo: `history`
 *  para procedência ("você já buscou isto" é o que a linha não diz sozinha),
 *  o tipo do lugar nos demais. Ausente ⇒ pino. */
export type PlaceSuggestion = {
  id: string;
  label: string;
  /** O CÓDIGO que abre a primeira linha em peso médio (`GRU São Paulo`) — o IATA
   *  nas praças do aéreo, receita da `LinhaDaPraca`. Ausente ⇒ a linha é só o
   *  `label`, que é o caso do hotel. */
  code?: string;
  description?: string;
  groupLabel?: string;
  icon?: 'history' | 'airport' | 'city' | 'lodging' | 'place' | 'office';
};

/** O ciclo da busca remota que alimenta o painel — o mesmo vocabulário do
 *  `HotelAsyncState` que o hotel já usava. */
export type PlaceSearchState = 'idle' | 'loading' | 'ready' | 'error';

export type PlacePanelCopy = {
  /** Nomeia o painel (aria-label do dialog) e o spinner do estado de voo. */
  label: string;
  emptyLabel: string;
  errorLabel: string;
  /** Rótulo do "tentar de novo" sob o erro. Ausente ⇒ o erro fica só na frase. */
  retryLabel?: string;
};

/** O glifo da linha. Relógio para o que veio do histórico (a procedência some
 *  quando o cabeçalho do grupo rola pra fora — padrão Google Flights/Booking),
 *  o tipo do lugar nos demais. Pino pra cidade e prédio pro lugar salvo pela
 *  organização — o mesmo mapa do protótipo (`GLIFO_DO_LUGAR`). */
const PLACE_GLYPH = {
  history: History,
  airport: Plane,
  city: MapPin,
  lodging: Hotel,
  place: MapPin,
  office: Building2,
} as const;

export function PlaceGlyph({ icon }: { readonly icon?: PlaceSuggestion['icon'] }) {
  const Glyph = PLACE_GLYPH[icon ?? 'place'];
  return <Glyph className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />;
}

/** A LINHA do painel — `py-1` no lugar do `py-1.5` do primitivo (pedido do
 *  designer, 2026-08-23) e `gap-2.5` entre glifo e texto: a receita da
 *  `LinhaDaPraca`/`PainelDeLugares` do desenho, que fecha a linha de duas
 *  frases em 44px. A de UMA frase (as paradas do ônibus, sem legenda) fecha
 *  em 28: no toque o `pointer-coarse:min-h-11` dá a ela os mesmos 44 de alvo
 *  (#6188); no mouse a medida segue a do desenho, e o teto do popover também.
 *  Exportada pra linha que o host injeta como `header` (a "perto de mim" do
 *  hotel) sair com a mesma medida das demais. */
export const PLACE_ROW_CLASS = 'items-center gap-2.5 py-1 pointer-coarse:min-h-11';

/**
 * A barra da lista é a `scrollbar-rail` do DS. Os `!` desfazem o `no-scrollbar`
 * do primitivo (`scrollbar-width: none` + `display: none` na barra) — sem eles
 * a barra nem nasce.
 *
 * SEM `scrollbar-gutter: stable`, de propósito: o gutter reservado era uma
 * faixa branca de 16px à direita das linhas sempre que a lista NÃO rolava (o
 * caso comum — dois recentes, três sugestões). A barra só ocupa espaço quando
 * existe; quando ela nasce ou morre ao digitar, as linhas truncam, então o que
 * se move é só o ponto de corte do texto.
 */
export const PLACE_LIST_SCROLLBAR_CLASS =
  'scrollbar-rail [scrollbar-width:auto]! [&::-webkit-scrollbar]:block! [scrollbar-gutter:auto]!';

/**
 * A altura da lista de lugares — o popover fecha no PÉ DA SEGUNDA LINHA de
 * sugestões (decisão do designer, 2026-08-23) e o que vier depois rola atrás
 * da barra de rolagem. Os 15.25rem são a conta medida no computado: rótulo
 * (34px) + 2 linhas de 44 + rótulo (34px) + 2 linhas de 44 = 244px. O corte no
 * meio da lista é proposital — junto com a barra, é o que anuncia o scroll.
 * Literal aqui e no `PlaceField` (que ainda a capa pela `--available-height`
 * do Base UI): o scanner do Tailwind só enxerga classe escrita por inteiro.
 */
const PLACE_LIST_CLASS = `max-h-[15.25rem] ${PLACE_LIST_SCROLLBAR_CLASS}`;

/** Voo e erro FORA do `CommandEmpty`, de propósito: o `Empty` do cmdk só
 *  renderiza com zero itens registrados no root, e um `header` com
 *  `CommandItem` (a linha "perto de mim") já conta — dentro dele, spinner e
 *  alerta sumiriam enquanto o header estivesse em cena. A veste é a mesma do
 *  primitivo (`py-6 text-center text-sm`), então nada muda no olho. */
function PlaceNotice({ children }: { readonly children: ReactNode }) {
  return <div className="py-6 text-center text-sm">{children}</div>;
}

/**
 * As linhas do painel: spinner em voo, alerta no erro, vazio, ou os grupos.
 * Digitando, os grupos COLAPSAM num só — com termo, o cabeçalho separaria
 * resultados que não têm razão de estar separados (decisão que o protótipo já
 * tomou nos dois lados). Deve viver dentro de um `CommandList`.
 */
export function PlaceResults<S extends PlaceSuggestion>({
  copy,
  state,
  suggestions,
  selectedPlaceId,
  query,
  onPick,
  onRetry,
}: {
  readonly copy: PlacePanelCopy;
  readonly state: PlaceSearchState;
  readonly suggestions: readonly S[];
  readonly selectedPlaceId?: string;
  readonly query: string;
  readonly onPick: (place: S) => void;
  /** Re-dispara a busca do termo corrente no estado de erro — uma queda de
   *  segundos não pode exigir apagar e redigitar. */
  readonly onRetry?: () => void;
}) {
  if (state === 'loading') {
    return (
      <PlaceNotice>
        <Spinner aria-label={copy.label} className="mx-auto" />
      </PlaceNotice>
    );
  }
  if (state === 'error') {
    return (
      <PlaceNotice>
        <p role="alert" className="text-body">
          {copy.errorLabel}
        </p>
        {onRetry && copy.retryLabel ? (
          <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
            {copy.retryLabel}
          </Button>
        ) : null}
      </PlaceNotice>
    );
  }
  /* O VAZIO fica no `CommandEmpty`, também de propósito (ver `PlaceNotice`):
     em zero-query com header, "nenhum lugar" embaixo da linha de localização
     leria como defeito — o painel não está vazio. O count do cmdk é
     exatamente o juiz certo aqui. */
  if (suggestions.length === 0) {
    return (
      <CommandEmpty>
        <span className="text-body">{copy.emptyLabel}</span>
      </CommandEmpty>
    );
  }

  const groups = new Map<string, S[]>();
  suggestions.forEach((place) => {
    const group = query.trim() ? '' : (place.groupLabel ?? '');
    groups.set(group, [...(groups.get(group) ?? []), place]);
  });

  return [...groups].map(([groupLabel, places]) => (
    <CommandGroup key={groupLabel || 'ungrouped'} heading={groupLabel || undefined}>
      {places.map((place) => (
        <CommandItem
          key={place.id}
          value={place.id}
          aria-current={place.id === selectedPlaceId ? 'true' : undefined}
          onSelect={() => {
            onPick(place);
          }}
          className={PLACE_ROW_CLASS}
        >
          <PlaceGlyph icon={place.icon} />
          <span className="grid min-w-0 flex-1 leading-tight">
            {/* `text-ui` (14/20, peso médio) no título e `text-caption` (12/16)
                na legenda — os tokens tipográficos da linha no desenho. */}
            <span className="text-ui truncate text-foreground">
              {/* Código em peso médio abrindo a linha (`GRU São Paulo`) — só o
                  aéreo o traz; sem ele a linha é o rótulo puro do hotel. */}
              {place.code ? <span className="font-medium">{place.code}</span> : null}
              {place.code ? ' ' : ''}
              {place.label}
            </span>
            {/* Espaço-texto entre as linhas: no grid ele não gera caixa, mas
                entra no nome acessível — sem ele o leitor de tela emenda
                título e legenda numa palavra só. */}
            {place.description ? ' ' : ''}
            {place.description && (
              <span className="text-caption truncate text-muted-foreground">
                {place.description}
              </span>
            )}
          </span>
        </CommandItem>
      ))}
    </CommandGroup>
  ));
}

/**
 * O `Command` root da lista de lugar — o mesmo no popover e na folha do campo e
 * no cartão da folha do hotel, que monta a lista à mão. `key` na fronteira
 * zero-query ↔ busca: o cmdk realça o item #0 ao MONTAR, e a troca de exibição
 * (o header sai, os resultados entram) tem de devolver o realce ao primeiro
 * resultado — é o que faz o Enter escolher o destino digitado em vez de morrer
 * num item que sumiu. Na zero-query a opção ativa é o valor ATUAL, e não a
 * linha #0 (a "perto de mim"): Enter sem digitar mantém o destino, em vez de
 * pedir a posição e trocá-lo em silêncio. Valor fora da lista ⇒ nada ativo.
 *
 * O `defaultValue` só vale ao MONTAR, e o cmdk, quando a linha ativa sai de
 * cena, realça a primeira que sobrou. As sugestões zero-query podem chegar com
 * a lista aberta e mudar o valor atual de grupo (solto → "Recentes"): o React
 * troca a linha dele, e o Enter voltaria a pedir a posição. Por isso a lista
 * zero-query remonta quando o LUGAR do valor atual nela muda.
 */
export function PlaceCommand({
  query,
  suggestions,
  selectedPlaceId,
  label,
  className,
  children,
}: {
  readonly query: string;
  readonly suggestions: readonly PlaceSuggestion[];
  readonly selectedPlaceId: string | undefined;
  readonly label: string;
  readonly className?: string;
  readonly children: ReactNode;
}): ReactNode {
  const current = query ? undefined : suggestions.find((place) => place.id === selectedPlaceId);
  const zeroQueryKey = current ? `zero-query:in:${current.groupLabel ?? ''}` : 'zero-query:out';
  return (
    <Command
      key={query ? 'search' : zeroQueryKey}
      shouldFilter={false}
      label={label}
      {...(!query && selectedPlaceId !== undefined ? { defaultValue: selectedPlaceId } : {})}
      {...(className === undefined ? {} : { className })}
    >
      {children}
    </Command>
  );
}

/**
 * A casca do painel: o popover ancorado sob o campo (a MESMA receita visual do
 * `PlaceField` — `w-80`, `rounded-xl`, sombra, anel e o `p-1` de respiro que lá
 * é do `Command`) com a `CommandList` dentro, no mesmo teto e com a mesma
 * barra de rolagem.
 * `header` é a fatia acima dos resultados na exibição zero-query (a linha
 * "perto de mim" do hotel) — deve ser conteúdo de `CommandList` (grupos/itens
 * do cmdk), e a ausência não deixa buraco.
 *
 * Precisa viver dentro do `Command` root de quem compõe, ao lado do input do
 * campo — ver o doc do módulo.
 */
export function PlacePanel<S extends PlaceSuggestion>({
  copy,
  state,
  suggestions,
  selectedPlaceId,
  query,
  onPick,
  header,
}: {
  readonly copy: PlacePanelCopy;
  readonly state: PlaceSearchState;
  readonly suggestions: readonly S[];
  readonly selectedPlaceId?: string;
  readonly query: string;
  readonly onPick: (place: S) => void;
  readonly header?: ReactNode;
}): ReactNode {
  return (
    <div
      className="absolute top-full left-0 z-50 mt-2 w-80 rounded-xl bg-popover p-1 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10"
      role="dialog"
      aria-label={copy.label}
    >
      <CommandList className={PLACE_LIST_CLASS}>
        {header}
        <PlaceResults
          copy={copy}
          state={state}
          suggestions={suggestions}
          {...(selectedPlaceId === undefined ? {} : { selectedPlaceId })}
          query={query}
          onPick={onPick}
        />
      </CommandList>
    </div>
  );
}
